// ===== Onboarding / cuenta =====
export type WhatsappAccountStatus =
  | 'ACTIVE'
  | 'DISCONNECTED'
  | 'TOKEN_EXPIRING'
  | 'TOKEN_EXPIRED'
  | 'ERROR';

export type WhatsappQualityRating = 'UNKNOWN' | 'GREEN' | 'YELLOW' | 'RED';

export interface WhatsappPhoneResponse {
  id: number;
  phoneNumberId: string;
  displayPhoneNumber: string;
  verifiedName?: string | null;
  qualityRating?: WhatsappQualityRating | string | null;
  registered: boolean;
}

export interface WhatsappAccountResponse {
  id: number;
  wabaId: string;
  status: WhatsappAccountStatus | string;
  tokenExpiresAt?: string | null;
  lastError?: string | null;
  phones: WhatsappPhoneResponse[];
}

export interface ExchangeRequest {
  code: string;
}

/**
 * Plantilla de la WABA con su estado en Meta. Sólo las APPROVED son enviables — es lo
 * único que se puede mandar con la ventana de 24 h cerrada —, pero el listado devuelve
 * también las que están en revisión o rechazadas para que se vea qué pasó con ellas.
 */
export interface WhatsappTemplate {
  name: string;
  language: string;
  status: string;
  category: string;
  /** Cuerpo con los {{1}}, {{2}}… sin sustituir. */
  bodyText: string | null;
  /** Cantidad de inputs a pintar. */
  paramCount: number;
  /**
   * TEXT, IMAGE, VIDEO, DOCUMENT o LOCATION. null si no hay cabecera. Con IMAGE,
   * VIDEO o DOCUMENT hay que adjuntar el archivo: Meta rechaza el envío sin él.
   */
  headerFormat: WhatsappHeaderFormat | null;
  /** Texto de la cabecera sin sustituir. Sólo si headerFormat es TEXT. */
  headerText: string | null;
  /** Marcadores de la cabecera de texto. Meta permite 0 ó 1. */
  headerParamCount: number;
  /** Motivo del rechazo según Meta. null salvo status = REJECTED. */
  rejectedReason?: string | null;
}

/** Único estado enviable: el resto no llega a WhatsApp. */
export function isTemplateSendable(t: WhatsappTemplate): boolean {
  return t.status === 'APPROVED';
}

export type WhatsappHeaderFormat = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';

/** Formatos de cabecera que obligan a adjuntar un archivo antes de enviar. */
export const MEDIA_HEADER_FORMATS: readonly WhatsappHeaderFormat[] = ['IMAGE', 'VIDEO', 'DOCUMENT'];

export function requiresMediaHeader(
  format: WhatsappHeaderFormat | null | undefined
): boolean {
  return !!format && MEDIA_HEADER_FORMATS.includes(format);
}

/** Campos de cabecera comunes al envío en conversación y al inicio de una nueva. */
export interface TemplateHeaderFields {
  headerFormat?: string | null;
  /** Valor del {{1}} de una cabecera TEXT. */
  headerText?: string | null;
  /** Devuelto por POST /api/whatsapp/media. */
  headerMediaId?: string | null;
  /** Alternativa al id: URL pública alcanzable por Meta. */
  headerMediaLink?: string | null;
  /** Nombre visible del archivo. Sólo cabeceras DOCUMENT. */
  headerFilename?: string | null;
}

export interface SendTemplateRequest extends TemplateHeaderFields {
  templateName: string;
  language: string;
  params: string[];
}

/** Iniciar conversación con un número que nunca escribió. Sólo con plantilla. */
export interface StartConversationRequest extends TemplateHeaderFields {
  whatsappPhoneId: number;
  to: string;
  templateName: string;
  language: string;
  params: string[];
  /**
   * Ficha del CRM a la que se escribe, cuando se conoce con certeza (la ventana abierta
   * desde las acciones rápidas de un contacto). Sin esto el backend tiene que deducirla
   * del texto del teléfono, y acaba creando un duplicado si la ficha guardó el número con
   * otro formato. Se omite cuando el número se teclea a mano.
   */
  contactId?: number;
}

/** Respuesta de la subida del archivo de cabecera a Meta. */
export interface WhatsappMediaUploadResponse {
  mediaId: string;
}

/** Alta de plantilla en Meta. Queda PENDING hasta que la aprueben. */
export interface CreateTemplateRequest {
  /** Sólo minúsculas, números y guiones bajos. */
  name: string;
  language: string;
  /** MARKETING o UTILITY. */
  category: string;
  bodyText: string;
  /** Un ejemplo por cada {{n}} del cuerpo — Meta los exige para revisar. */
  bodyExamples: string[];
  headerText?: string;
  footerText?: string;
}

/**
 * Conexión manual (sólo pruebas): se usa mientras la app de Meta no está
 * aprobada como proveedor de tecnología y el Embedded Signup está bloqueado.
 */
export interface ManualConnectRequest {
  wabaId: string;
  accessToken: string;
  phoneNumberId?: string;
  displayPhoneNumber?: string;
  verifiedName?: string;
}

// ===== Asignaciones =====
export type WhatsappAssignmentRole = 'OWNER' | 'AGENT';

export interface AssignmentCreateRequest {
  userId: number;
  whatsappPhoneId: number;
  role?: WhatsappAssignmentRole;
}

export interface UserWhatsappAssignmentResponse {
  id: number;
  userId: number;
  whatsappPhoneId: number;
  role: WhatsappAssignmentRole | string;
}

// ===== Ajustes del canal =====

/**
 * Ajustes de WhatsApp del negocio.
 *
 * `connected` viene aparte del ajuste a propósito: el backend responde 200 aunque el
 * negocio no tenga cuenta conectada, para que la pantalla pueda pintar el interruptor
 * (deshabilitado) en vez de esconderlo sin explicación.
 */
export interface WhatsappSettingsResponse {
  /**
   * true = cada número desconocido que escribe crea su ficha de contacto automáticamente.
   * false (valor por defecto) = el mensaje llega igual a la bandeja, pero sin crear ficha.
   */
  autoCreateContacts: boolean;
  /** El negocio tiene una cuenta de WhatsApp ACTIVE. */
  connected: boolean;
}

export interface UpdateWhatsappSettingsRequest {
  autoCreateContacts: boolean;
}

/** Resultado de crear (o vincular) a mano la ficha de una conversación. */
export interface WhatsappConversationContactResponse {
  conversationId: number;
  contactId: number;
  contactName: string | null;
  /** true = ficha nueva; false = ya existía una con ese número y sólo se vinculó. */
  created: boolean;
  /** Mensajes del hilo que quedaron asociados al contacto. */
  linkedMessages: number;
}

/** Nombre opcional para la ficha nueva. Se ignora si el número ya tenía ficha. */
export interface CreateConversationContactRequest {
  fullname?: string;
}

// ===== Limpieza de contactos creados por WhatsApp =====

/**
 * Desglose de una limpieza: lo mismo que devuelve la previsualización y lo que devuelve la
 * ejecución ya consumada.
 *
 * OJO con dos parejas que se parecen y NO significan lo mismo:
 *  - `deals` / `tasks` son registros que SE BORRAN (colgaban sólo de estos contactos).
 *  - `dealContactLinks` / `taskContactLinks` son negocios y tareas que SE CONSERVAN —
 *    están compartidos con algún contacto que sobrevive— y de los que sólo se retira la
 *    fila puente con el contacto borrado.
 */
export interface WhatsappContactCleanupCounts {
  /** Contactos que se borrarían. Es el número que hay que devolver como `expectedContacts`. */
  contacts: number;
  // --- Registros que desaparecen ---
  deals: number;
  leads: number;
  tickets: number;
  tasks: number;
  quotes: number;
  quoteApprovals: number;
  contactCustomAttributes: number;
  campaignRecipients: number;
  campaignManualRecipients: number;
  campaignExcludedContacts: number;
  formSubmissions: number;
  // --- Vínculos retirados: el registro del otro lado se conserva ---
  dealContactLinks: number;
  taskContactLinks: number;
  contactCompanyLinks: number;
  contactSegmentMembers: number;
  // --- Lo que sobrevive intacto ---
  preservedConversations: number;
  preservedMessages: number;
}

/** El recuento tiene que venir de la previsualización: sin él el backend no ejecuta. */
export interface ExecuteContactCleanupRequest {
  expectedContacts: number;
}

export interface WhatsappContactCleanupResult {
  counts: WhatsappContactCleanupCounts;
  executedAt: string;
  executedByUserId: number;
}

// ===== Conversaciones =====
export interface WhatsappConversation {
  id: number;
  whatsappPhoneId: number;
  contactId?: number | null;
  /**
   * Nombre del contacto vinculado, resuelto por el backend para toda la página de una vez.
   * null cuando el hilo no tiene contacto o su ficha no tiene nombre: la bandeja cae al número.
   */
  contactName?: string | null;
  waId: string;
  customerWindowUntil?: string | null;
  lastMessageAt?: string | null;
}

// ===== Mensajes =====
export type WhatsappMessageDirection = 'INBOUND' | 'OUTBOUND';

export type WhatsappMessageStatus =
  | 'SENT'
  | 'DELIVERED'
  | 'READ'
  | 'FAILED'
  | 'RECEIVED'
  | 'PENDING';

export type WhatsappMessageType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'document'
  | string;

export interface WhatsappMessageResponse {
  id: number;
  conversationId: number;
  waMessageId?: string | null;
  direction: WhatsappMessageDirection | string;
  type: WhatsappMessageType;
  body?: string | null;
  mediaPath?: string | null;
  status: WhatsappMessageStatus | string;
  messageTimestamp: string;
  sentByUserId?: number | null;
  /**
   * Motivo que dio Meta cuando el estado es FAILED (p. ej. "131030 - Recipient phone
   * number not in allowed list"). Null en cualquier otro estado. Sin esto la burbuja
   * sólo puede decir "Fallido", que no le dice al asesor qué hacer.
   */
  errorMessage?: string | null;
}

/**
 * Envío libre: texto, adjunto, o adjunto con pie.
 *
 * Para adjuntar hay que subir antes el archivo con `uploadMedia` y mandar el `mediaId`
 * que devuelve junto con su `mediaType`. Cuando hay adjunto, `text` viaja como pie de
 * foto — y sólo lo admiten image, video y document.
 */
export interface SendMessageRequest {
  text?: string;
  mediaId?: string;
  mediaType?: WhatsappSendableMediaType;
  /** Sólo para `document`: el nombre con el que el cliente ve el archivo. */
  filename?: string;
}

/** Tipos de adjunto que se pueden enviar (los mismos que acepta la Cloud API). */
export type WhatsappSendableMediaType =
  | 'image'
  | 'audio'
  | 'video'
  | 'document'
  | 'sticker';

/** Tipos cuya burbuja se pinta con el archivo y no sólo con texto. */
export const WHATSAPP_MEDIA_TYPES: readonly string[] = [
  'image',
  'audio',
  'video',
  'document',
  'sticker',
];

export function isWhatsappMediaMessage(message: WhatsappMessageResponse): boolean {
  return !!message.mediaPath && WHATSAPP_MEDIA_TYPES.includes(message.type);
}

// ===== Etiquetas legibles (español) =====
/**
 * Diccionario único del módulo WhatsApp. Ninguna tabla, panel o badge debe
 * pintar la llave técnica: todo pasa por `whatsappLabel` con uno de estos mapas.
 */
export function whatsappLabel(
  dict: Readonly<Record<string, string>>,
  key: string | null | undefined,
  fallback = '—'
): string {
  if (key === null || key === undefined || key === '') return fallback;
  return dict[key] ?? key;
}

export const WHATSAPP_ACCOUNT_STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: 'Conectada',
  DISCONNECTED: 'Desconectada',
  TOKEN_EXPIRING: 'Token por vencer',
  TOKEN_EXPIRED: 'Token vencido',
  ERROR: 'Con error',
};

export const WHATSAPP_QUALITY_RATING_LABELS: Readonly<Record<string, string>> = {
  UNKNOWN: 'Sin calificar',
  GREEN: 'Calidad alta',
  YELLOW: 'Calidad media',
  RED: 'Calidad baja',
};

export const WHATSAPP_TEMPLATE_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidad',
  AUTHENTICATION: 'Autenticación',
  TRANSACTIONAL: 'Transaccional',
  OTP: 'Código de verificación',
};

export const WHATSAPP_TEMPLATE_STATUS_LABELS: Readonly<Record<string, string>> = {
  APPROVED: 'Aprobada',
  PENDING: 'En revisión',
  REJECTED: 'Rechazada',
  PAUSED: 'Pausada',
  DISABLED: 'Deshabilitada',
  IN_APPEAL: 'En apelación',
};

export const WHATSAPP_HEADER_FORMAT_LABELS: Readonly<Record<string, string>> = {
  TEXT: 'Texto',
  IMAGE: 'Imagen',
  VIDEO: 'Video',
  DOCUMENT: 'Documento',
  LOCATION: 'Ubicación',
};

export const WHATSAPP_ASSIGNMENT_ROLE_LABELS: Readonly<Record<string, string>> = {
  OWNER: 'Propietario',
  AGENT: 'Agente',
};

export const WHATSAPP_MESSAGE_TYPE_LABELS: Readonly<Record<string, string>> = {
  text: 'Texto',
  image: 'Imagen',
  video: 'Video',
  audio: 'Audio',
  document: 'Documento',
  sticker: 'Sticker',
  location: 'Ubicación',
  contacts: 'Contacto',
  template: 'Plantilla',
  interactive: 'Mensaje interactivo',
  button: 'Botón',
  reaction: 'Reacción',
  unsupported: 'Formato no soportado',
};

export const WHATSAPP_MESSAGE_STATUS_LABELS: Readonly<Record<string, string>> = {
  PENDING: 'Pendiente',
  SENT: 'Enviado',
  DELIVERED: 'Entregado',
  READ: 'Leído',
  RECEIVED: 'Recibido',
  FAILED: 'Falló',
};

export const WHATSAPP_MESSAGE_DIRECTION_LABELS: Readonly<Record<string, string>> = {
  INBOUND: 'Entrante',
  OUTBOUND: 'Saliente',
};
